import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { OrgStatus } from '@construction/shared';
import { orgsApi, type Org, type OrgRef } from '@/api/endpoints';
import { Field, Select } from '@/components/ui';
import { FilterChip } from '@/features/filters/FilterChip';
import { usePermissions } from '@/lib/permissions';
import { groupSitesByOrg } from './group-sites';

export { groupSitesByOrg } from './group-sites';

/**
 * 单位（CR-073）在页面上的两样公共件：列表查询、「归哪家」下拉。
 *
 * **只有平台账号的界面上出现它们**。单位管理员看到的就是「全部」—— 范围服务端已经圈好，
 * 界面上连「公司」两个字都不出现（客户要的是四家各自一套的感觉，不是一套系统里的一个分区）。
 */
export function useOrgs() {
  const { isPlatform } = usePermissions();
  const query = useQuery({ queryKey: ['orgs'], queryFn: () => orgsApi.list(), enabled: isPlatform });
  const all: Org[] = query.data ?? [];
  return {
    ...query,
    all,
    /** 能往里建东西的（归档的不行，服务端回 ORG_ARCHIVED） */
    active: all.filter((org) => org.status === OrgStatus.ACTIVE),
  };
}

/**
 * 工地切换器里的选项（CR-076，客户选的：「同一个切换器里按公司分组」）。网页端左上角和 App 首页两个切换器共用。
 *
 * 平台账号看得到所有公司的工地，平铺一列看不出哪个是哪家的（两家都有「1号楼」就分不清了）——
 * 按公司分组（`<optgroup>`）。单位账号只有自己家的工地，照旧平铺，界面上不出现「公司」。
 * **工地仍然只有这一个来源**（约束 6）：分组只是排版，没有第二个「当前公司」状态要同步。
 */
export function SiteOptions<T extends { id: number; name: string; orgId: number; org: OrgRef }>({
  sites,
}: {
  sites: readonly T[];
}) {
  const { isPlatform } = usePermissions();
  const option = (site: T) => (
    <option key={site.id} value={site.id}>
      {site.name}
    </option>
  );
  if (!isPlatform) return <>{sites.map(option)}</>;
  return (
    <>
      {groupSitesByOrg(sites).map((group) => (
        <optgroup key={group.org.id} label={group.org.name}>
          {group.sites.map(option)}
        </optgroup>
      ))}
    </>
  );
}

/** 下拉的值：`''` = 还没选（平台账号必须选，不给默认值），`null` = 平台 / 共享，数字 = 那一家 */
export type OrgChoice = number | null | '';

/**
 * 「归哪家」下拉。建人、建角色、建工地三张表单共用（CR-073）。
 *
 * - **默认不选、必选**：默认值会让人不看就点保存，把人建到错的那家；
 * - `platformOption` 给了才出现「平台 / 共享」那一项（只有超管，而且只对人和角色有意义 —— 平台自己没有工地）；
 * - 只列没归档的；一家都没有时提示先去建一家。
 */
export function OrgSelect({
  id,
  label,
  value,
  onChange,
  platformOption,
  hint,
}: {
  id: string;
  label: string;
  value: OrgChoice;
  onChange: (value: OrgChoice) => void;
  platformOption?: string;
  hint?: string;
}) {
  const { t } = useTranslation();
  const { active } = useOrgs();
  const empty = active.length === 0;

  return (
    <Field label={label} htmlFor={id} required hint={empty ? t('org.noneYet') : hint}>
      <Select
        id={id}
        value={value === '' ? '' : value === null ? 'platform' : String(value)}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw === '' ? '' : raw === 'platform' ? null : Number(raw));
        }}
        required
      >
        <option value="">{t('org.pick')}</option>
        {platformOption && <option value="platform">{platformOption}</option>}
        {active.map((org) => (
          <option key={org.id} value={org.id}>
            {org.name}
          </option>
        ))}
      </Select>
    </Field>
  );
}

/** 列表页顶上的「按公司筛」：全部 / 平台（可选）/ 每一家（含归档的，查历史要用） */
export function OrgFilter({
  value,
  onChange,
  platformOption,
}: {
  /** 以前给 <label htmlFor> 用；CR-082 改成筛选胶囊之后不用了，留着参数不动调用方 */
  id?: string;
  /** `''` = 全部，`platform`，或单位 id 的字符串 —— 和接口的 `org` 参数一个形状 */
  value: string;
  onChange: (value: string) => void;
  platformOption?: string;
}) {
  const { t } = useTranslation();
  const { all } = useOrgs();
  // 筛选胶囊（CR-082）：「公司 全部」，和页面上其它筛选一个样子
  return (
    <FilterChip<string>
      label={t('org.column')}
      value={value || null}
      onChange={(next) => onChange(next ?? '')}
      options={[
        { value: null, label: t('org.all') },
        ...(platformOption ? [{ value: 'platform', label: platformOption }] : []),
        ...all.map((org) => ({ value: String(org.id), label: org.name })),
      ]}
    />
  );
}
